import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import AddLine from '@/assets/icons/ri_add_line.svg?component';
import RefreshLine from '@/assets/icons/ri_refresh_line.svg?component';
import SubtractLine from '@/assets/icons/ri_subtract_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import type { StageZoom } from '../../hooks/useStageZoom';

import styles from './ZoomControls.module.scss';

export interface ZoomControlsProps {
    zoom: StageZoom;
    /** While the magnifier is on the image is held at full size. */
    disabled: boolean;
}

/**
 * Zoom in, zoom out and reset in the image's top right corner, for readers without a wheel
 * or a pinch. aria-disabled rather than disabled at the ends of the range, so the button a
 * keyboard user just pressed keeps focus.
 */
export default function ZoomControls(props: ZoomControlsProps) {
    const { zoom, disabled } = props;
    const { t } = useTranslation();

    const canZoomIn = !disabled && zoom.canZoomIn;
    const canZoomOut = !disabled && zoom.isZoomed;

    return (
        <div
            className={classNames(styles.zoomControls, { [styles.zoomed]: zoom.isZoomed })}
            role="group"
            aria-label={t('imageViewer.zoom.group')}>
            <Button
                className={classNames(styles.zoomButton, styles.stepButton)}
                variant="overlay"
                size="small"
                iconOnly
                aria-label={t('imageViewer.zoom.in')}
                title={t('imageViewer.zoom.in')}
                trackingId="image_viewer_zoom_in"
                aria-disabled={!canZoomIn}
                onClick={canZoomIn ? zoom.zoomIn : undefined}
                icon={
                    <SvgIcon
                        icon={AddLine}
                        size="inherit"
                        svgProps={{ 'aria-hidden': true, 'focusable': 'false' }}
                    />
                }
            />
            <Button
                className={classNames(styles.zoomButton, styles.stepButton)}
                variant="overlay"
                size="small"
                iconOnly
                aria-label={t('imageViewer.zoom.out')}
                title={t('imageViewer.zoom.out')}
                trackingId="image_viewer_zoom_out"
                aria-disabled={!canZoomOut}
                onClick={canZoomOut ? zoom.zoomOut : undefined}
                icon={
                    <SvgIcon
                        icon={SubtractLine}
                        size="inherit"
                        svgProps={{ 'aria-hidden': true, 'focusable': 'false' }}
                    />
                }
            />
            <Button
                className={classNames(styles.zoomButton, styles.resetButton)}
                variant="overlay"
                size="small"
                iconOnly
                aria-label={t('imageViewer.zoom.reset')}
                title={t('imageViewer.zoom.reset')}
                trackingId="image_viewer_zoom_reset"
                aria-disabled={!canZoomOut}
                onClick={canZoomOut ? zoom.reset : undefined}
                icon={
                    <SvgIcon
                        icon={RefreshLine}
                        size="inherit"
                        svgProps={{ 'aria-hidden': true, 'focusable': 'false' }}
                    />
                }
            />
        </div>
    );
}
